import React, { useState, useRef, useEffect } from 'react';

interface VerificationCodeModalProps {
  phoneNumber: string;
  onBack: () => void;
  onVerify: (code: string) => void;
}

export default function VerificationCodeModal({
  phoneNumber,
  onBack,
  onVerify,
}: VerificationCodeModalProps) {
  const [code, setCode] = useState(['', '', '', '']);
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // Try to use Web OTP API for autofill (if available)
  useEffect(() => {
    if ('OTPCredential' in window) {
      const ac = new AbortController();
      
      navigator.credentials
        .get({
          // @ts-ignore - OTPCredential is not in TypeScript types yet
          otp: { transport: ['sms'] },
          signal: ac.signal,
        })
        .then((otp: any) => {
          if (otp?.code) {
            const digits = otp.code.slice(0, 4).split('');
            setCode(digits);
            // Autofill detected, can auto-verify
            if (digits.length === 4) {
              setTimeout(() => onVerify(digits.join('')), 500);
            }
          }
        })
        .catch((err: any) => {
          console.log('OTP autofill not available:', err);
        });

      return () => {
        ac.abort();
      };
    }
  }, [onVerify]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-verify when all 4 digits are entered
    if (index === 3 && value) {
      const fullCode = newCode.join('');
      if (fullCode.length === 4) {
        setTimeout(() => onVerify(fullCode), 300);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        // If current input is empty, move to previous input
        inputRefs[index - 1].current?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    
    if (/^\d+$/.test(pastedData)) {
      const digits = pastedData.split('');
      const newCode = [...digits, '', '', ''].slice(0, 4);
      setCode(newCode);
      
      // Focus the next empty input or last input
      const nextIndex = Math.min(digits.length, 3);
      inputRefs[nextIndex].current?.focus();

      // Auto-verify if all 4 digits were pasted
      if (digits.length === 4) {
        setTimeout(() => onVerify(newCode.join('')), 300);
      }
    }
  };

  const handleVerifyClick = () => {
    const fullCode = code.join('');
    if (fullCode.length === 4) {
      onVerify(fullCode);
    }
  };

  return (
    <>
      {/* 50% Gray Overlay */}
      <div className="fixed inset-0 bg-[#D9D9D9] bg-opacity-50 z-40" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-[20px] shadow-lg p-6 w-full max-w-[320px]">
          {/* Heading */}
          <h3 className="text-[16px] font-bold text-[#28292d] text-center mb-6">
            Enter 4-digit code
          </h3>

          {/* Code Inputs */}
          <div className="flex justify-center gap-4 mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-[50px] h-[60px] text-center text-[24px] font-bold text-[#28292d] border-2 border-[#b9babf] rounded-[8px] focus:outline-none focus:border-[#0156FF]"
                autoComplete="one-time-code"
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex justify-between gap-3">
            <button
              onClick={onBack}
              className="flex-1 h-[40px] bg-[#8E8E8F] text-white text-[16px] font-bold rounded-full active:scale-[0.98] transition-transform"
            >
              Back
            </button>
            <button
              onClick={handleVerifyClick}
              disabled={code.join('').length !== 4}
              className={`flex-1 h-[40px] text-white text-[16px] font-bold rounded-full transition-all ${
                code.join('').length === 4
                  ? 'bg-[#0156FF] active:scale-[0.98]'
                  : 'bg-[#b9babf] cursor-not-allowed'
              }`}
            >
              Verify
            </button>
          </div>

          {/* Helper text */}
          <p className="text-[12px] text-[#8e8e8f] text-center mt-4">
            Code sent to {phoneNumber}
          </p>
        </div>
      </div>
    </>
  );
}

